import React from "react";
import {Button} from "antd";
import dayjs from "dayjs";

import {getBookingIncrement} from "../../helpers/booking";
import {messageType, showMessages} from "../../helpers/message";
import {useUpdateBookingMutation} from "../../store/api/bookingsApi";
import {BookingAddIncrementParams} from "../../types/bookingTypes";
import {BaseError} from "../../types/apiTypes";

const BookingAddIncrement = ({bookingID, bookingDetails, onBookingUpdate}: BookingAddIncrementParams) => {
    const [addIncrementText, setAddIncrementText] = React.useState<string | undefined>(undefined);
    const bookingIncrement = getBookingIncrement();
    const incrementInMinutes = bookingIncrement / 60; // getting booking increment in minutes
    const units = 'minutes';

    const [updateBooking, {isSuccess, isError, error, isLoading}] = useUpdateBookingMutation();

    React.useEffect(() => {
        if (isSuccess) {
            showMessages(incrementInMinutes + ' ' + units + ' have successfully been added to your trip.', messageType.success);
        }
        if (isError) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [error, isError, isSuccess, incrementInMinutes]);


    React.useEffect(() => {
        const setIncrementButtonText = () => {
            setAddIncrementText('Add ' + incrementInMinutes + ' Minutes')
        };

        if (bookingIncrement !== null) {
            setIncrementButtonText()
        }
    }, [bookingIncrement, incrementInMinutes])

    const canExtendBooking = () => {
        if (bookingDetails.ended_at === null) {
            showMessages('Cannot add time to an open return booking!', messageType.error);
            return false;
        }
        const oldEndedAt = dayjs(bookingDetails.ended_at).tz();
        const now = dayjs().tz().startOf('minute');

        // Cannot extend a cancelled booking.
        if (bookingDetails.state === 'CANCELLED') {
            showMessages('Cannot add time to a cancelled booking!', messageType.error);
            return false;
        }

        // Cannot extend booking if booking has ended.
        if (oldEndedAt.isBefore(now)) {
            showMessages('Cannot add time to a booking that has ended!', messageType.error);
            return false;
        }

        return true;
    };

    const handleClick = async () => {
        if (!canExtendBooking()) {
            return;
        }

        try {
            const newEndTime = dayjs(bookingDetails.ended_at).tz().add(incrementInMinutes, 'minutes').format('YYYY-MM-DD HH:mm:ssZZ');
            const requestData = {'ended_at': newEndTime};

            await updateBooking({bookingID, requestData});
            if (onBookingUpdate) onBookingUpdate(); // For the manual refetch needed on the MyBookings/BookingList card.

        } catch (error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    };

    return (
        <Button
            ghost
            type='primary'
            htmlType='button'
            onClick={handleClick}
            loading={isLoading}
        >
            {addIncrementText}
        </Button>
    );
}

export default BookingAddIncrement;
